import React, { useEffect, useState, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { openCreateProjectNoteDialog } from '../utils/create-project-note-events';
import { NOTES_CONTENT_MAX_LENGTH, type MoveToNoteResult } from '../utils/note-utils';
import {
  getFeedbackAnalyzeDisableReason,
  getFeedbackAnalyzeDisableTooltip,
} from '../utils/feedback-analyze-availability';
import { readChatSelectionFromEditor } from '../utils/chat-text-context';
import { getDocumentMarkdownText, getDocumentPlainText } from '../utils/document-plain-text';
import { useSettings } from '../contexts/SettingsContext';
import { useFocusMode } from '../contexts/FocusModeContext';
import { useIsFormattingBarBlocked } from '../hooks/use-formatting-bar-blocked';
import { Link } from 'react-router-dom';
import { MessageSquare, Lightbulb, BookType, MoreVertical, Bot, Loader2, Volume2 } from 'lucide-react';
import { CONCEPT_ICONS } from '../utils/concept-icons';
import { RightSidebarContext } from '../contexts/RightSidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { toBcp47Locale } from '../locales/locale';
import {
  ensureDocumentSpeechLanguage,
  isDocumentSpeechSupported,
  speakDocumentText,
} from '../utils/document-speech';
import { trackFeatureUsage } from '../utils/track-feature-usage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  COLOR_FORMATTING_BAR_SEPARATOR,
  COLOR_FORMATTING_BAR_TEXT,
  COLOR_FORMATTING_BAR_TEXT_ACTIVE,
  COLOR_FORMATTING_BAR_HOVER,
} from '../colors';
import { STICKY_GLASS_CHROME_CLASSNAME } from '../lib/sticky-glass';
import { cn } from '../lib/utils';

const FORMATTING_BAR_Z_INDEX = 1990;

type FormattingAction = 'format' | 'comment' | 'analyze' | 'synonyms' | 'chat' | 'readAloud';

export type { MoveToNoteResult };

interface FormattingBarProps {
  editor: Editor | null;
  projectId?: string | null;
  documentId?: string | null;
  onMoveToNote?: (content: string) => Promise<MoveToNoteResult>;
  onCommentClick?: () => void;
  onAnalyzeClick?: () => void;
  onSynonymsClick?: () => void;
  isAnalyzing?: boolean;
  actions?: FormattingAction[];
  mode?: 'default' | 'pure';
}

interface FormatButton {
  name: string;
  label: string;
  icon: React.ReactNode;
  command: () => void;
  isActive: boolean;
  hasSeparatorBefore?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

export const FormattingBar: React.FC<FormattingBarProps> = ({
  editor,
  projectId,
  documentId = null,
  onMoveToNote,
  onCommentClick,
  onAnalyzeClick,
  onSynonymsClick,
  isAnalyzing = false,
  actions = ['format', 'comment', 'analyze', 'synonyms', 'chat', 'readAloud'],
  mode = 'default',
}) => {
  const { t, locale } = useLocale();
  const { token, masterKey } = useAuth();
  const { showToast, showErrorToast, hideToast } = useToast();
  const { writingSettings } = useSettings();
  const { setIsFocusMode } = useFocusMode();
  const isPureMode = mode === 'pure';
  const effectiveActions: FormattingAction[] = isPureMode ? ['format'] : actions;
  const shouldShowLists = isPureMode || writingSettings.enableLists;
  const shouldShowFootnote = !isPureMode && effectiveActions.includes('format');
  const shouldShowMoveToNote = !isPureMode && Boolean(projectId && onMoveToNote) && effectiveActions.includes('format');
  const NoteIcon = CONCEPT_ICONS.note;
  const [show, setShow] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const barRef = useRef<HTMLDivElement>(null);
  const showRef = useRef(show);
  const hasSelectionRef = useRef(hasSelection);
  showRef.current = show;
  hasSelectionRef.current = hasSelection;
  const suppressShowRef = useRef(false);
  const sidebarContext = useContext(RightSidebarContext);
  const sidebarOpen = sidebarContext ? (sidebarContext.open || sidebarContext.openMobile) : false;
  const activeView = sidebarContext?.activeView ?? null;
  const activeMainAreaView = sidebarContext?.activeMainAreaView ?? null;
  const isBlockedByEditorOverlay = useIsFormattingBarBlocked();

  const openChatWithCurrentSelection = (): void => {
    if (!editor || !sidebarContext?.openChat) {
      return;
    }

    const { from, to } = editor.state.selection;
    const selection = readChatSelectionFromEditor(
      (start, end) => getDocumentMarkdownText(editor.state.doc, start, end),
      from,
      to,
    );
    setIsFocusMode(false);
    sidebarContext.openChat({ selection });
  };

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!editor) return;

    const updatePosition = () => {
      const { state } = editor;
      const { selection } = state;
      const { from, to } = selection;

      const hideBarWithoutSelection = () => {
        if (!hasSelectionRef.current && !showRef.current) {
          return;
        }
        setHasSelection(false);
        setShow(false);
      };

      if (from === to) {
        hideBarWithoutSelection();
        return;
      }

      const { doc } = state;
      let hasImageNode = false;

      if (selection instanceof NodeSelection) {
        if (selection.node && (selection.node.type.name === 'image' || selection.node.type.name === 'imageResize')) {
          hideBarWithoutSelection();
          return;
        }
      }

      doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === 'image' || node.type.name === 'imageResize') {
          hasImageNode = true;
        }
      });

      if (hasImageNode) {
        hideBarWithoutSelection();
        return;
      }

      setHasSelection(true);

      const { view } = editor;
      
      try {
        const startPos = view.coordsAtPos(from);
        const endPos = view.coordsAtPos(to);
        
        const selectionCenter = (startPos.left + endPos.right) / 2;
        let left = selectionCenter;
        
        const viewportWidth = window.innerWidth;
        const padding = 10;
        const estimatedBarWidth = Math.min(400, viewportWidth - padding * 2);
        const estimatedHalfWidth = estimatedBarWidth / 2;
        
        if (left + estimatedHalfWidth > viewportWidth - padding) {
          left = viewportWidth - padding - estimatedHalfWidth;
        }
        if (left - estimatedHalfWidth < padding) {
          left = padding + estimatedHalfWidth;
        }
        
        if (isMobile) {
          const top = endPos.bottom + 10;
          setPosition({ left, top });
        } else {
          const top = startPos.top - 50;
          setPosition({ left, top });
        }

        if (suppressShowRef.current) {
          hideBarWithoutSelection();
          return;
        }
        setShow(true);
      } catch (error) {
        setShow(false);
      }
    };

    const hideBar = () => {
      // Small delay to allow button clicks
      setTimeout(() => {
        const { state } = editor;
        if (!state.selection.empty) {
          // Still has selection, don't hide
          return;
        }
        setShow(false);
      }, 100);
    };

    // Update position on selection changes
    editor.on('selectionUpdate', updatePosition);
    editor.on('transaction', updatePosition);
    
    // Hide when editor loses focus (but only if no selection)
    const handleBlur = () => {
      const { state } = editor;
      if (state.selection.empty) {
        setShow(false);
      }
    };
    
    editor.on('blur', handleBlur);

    // Handle clicks outside the editor
    const handleClickOutside = (event: MouseEvent) => {
      const { view } = editor;
      const editorElement = view.dom;
      const target = event.target as Node;
      
      if (!editorElement.contains(target) && !barRef.current?.contains(target)) {
        const { state } = editor;
        if (state.selection.empty) {
          setShow(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Initial check
    updatePosition();

    return () => {
      editor.off('selectionUpdate', updatePosition);
      editor.off('transaction', updatePosition);
      editor.off('blur', handleBlur);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editor, isMobile]);

  useEffect(() => {
    if (!show) return;

    const adjustPosition = (): void => {
      if (!barRef.current) {
        return;
      }

      const barRect = barRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 10;

      let { left, top } = position;

      if (barRect.width === 0) {
        return;
      }

      const halfWidth = barRect.width / 2;
      const actualRight = barRect.right;

      if (actualRight > viewportWidth - padding) {
        left = viewportWidth - padding - halfWidth;
      }

      const actualLeft = barRect.left;
      if (actualLeft < padding) {
        left = padding + halfWidth;
      }

      const calculatedRightEdge = left + halfWidth;
      if (calculatedRightEdge > viewportWidth - padding) {
        left = viewportWidth - padding - halfWidth;
      }

      const calculatedLeftEdge = left - halfWidth;
      if (calculatedLeftEdge < padding) {
        left = padding + halfWidth;
      }

      const actualTop = barRect.top;
      const actualBottom = barRect.bottom;

      if (actualTop < padding) {
        top = padding;
      }
      if (actualBottom > viewportHeight - padding) {
        top = viewportHeight - padding - barRect.height;
      }

      if (Math.abs(left - position.left) > 0.5 || Math.abs(top - position.top) > 0.5) {
        setPosition({ left, top });
      }
    };

    const runAdjustment = (): void => {
      if (!barRef.current) {
        return;
      }
      adjustPosition();
    };

    const timeoutId1 = setTimeout(runAdjustment, 0);
    const timeoutId2 = setTimeout(runAdjustment, 10);
    const rafId = requestAnimationFrame(() => {
      setTimeout(runAdjustment, 0);
    });

    const resizeObserver = new ResizeObserver(() => {
      runAdjustment();
    });
    const handleResize = (): void => {
      runAdjustment();
    };
    const handleScroll = (): void => {
      runAdjustment();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    if (barRef.current) {
      resizeObserver.observe(barRef.current);
    }

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [show, position, isMobile]);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const modKey = isMac ? '⌘' : 'Ctrl';
  const optKey = isMac ? '⌥' : 'Alt';
  const iconSize = isMobile ? '12' : '16';
  const lucideIconSize = isMobile ? 'h-4 w-4' : 'h-6 w-6';

  useEffect(() => {
    if (!editor) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const editorElement = editor.view.dom as HTMLElement | null;
      const target = event.target as Node | null;

      if (!editorElement || !target || !editorElement.contains(target)) {
        return;
      }

      const isMod = isMac ? event.metaKey : event.ctrlKey;
      const isAlt = event.altKey;
      const isShift = event.shiftKey;
      const key = event.key.toLowerCase();

      if (!isMod || !isAlt) {
        return;
      }

      const getSelectedTextStats = () => {
        const selectedText = getDocumentPlainText(
          editor.state.doc,
          editor.state.selection.from,
          editor.state.selection.to,
          ' ',
        );
        const trimmedText = selectedText.trim();
        const wordCount = trimmedText.split(/\s+/).filter((w) => w.length > 0).length;
        const sentenceCount = trimmedText.split(/[.!?]+/).filter((s) => s.trim().length > 0).length;
        return { wordCount, sentenceCount };
      };

      if (!isShift && key === 's' && effectiveActions.includes('synonyms') && onSynonymsClick) {
        const { wordCount, sentenceCount } = getSelectedTextStats();
        const isDisabled = wordCount > 35 || sentenceCount > 3;
        if (isDisabled) {
          return;
        }
        event.preventDefault();
        onSynonymsClick();
        setShow(false);
        return;
      }

      if (!isShift && key === 'a' && effectiveActions.includes('analyze') && onAnalyzeClick) {
        if (isAnalyzing) {
          return;
        }
        if (getFeedbackAnalyzeDisableReason(editor) !== null) {
          return;
        }
        event.preventDefault();
        onAnalyzeClick();
        return;
      }

      if (!isShift && key === 'm' && effectiveActions.includes('comment') && onCommentClick) {
        const { from, to } = editor.state.selection;
        if (from === to) {
          return;
        }
        event.preventDefault();
        onCommentClick();
        setShow(false);
        return;
      }

      if (!isShift && key === 'd' && effectiveActions.includes('chat') && sidebarContext?.openChat) {
        event.preventDefault();
        suppressShowRef.current = true;
        setTimeout(() => {
          suppressShowRef.current = false;
        }, 500);
        openChatWithCurrentSelection();
        setShow(false);
        return;
      }

      if (!isShift && key === 'f' && shouldShowFootnote) {
        event.preventDefault();
        try {
          const { state } = editor;
          const { selection } = state;
          const { from, to } = selection;

          if (from !== to) {
            editor.chain()
              .setTextSelection(to)
              .focus()
              .addFootnote()
              .run();
          } else {
            (editor.chain().focus() as any).addFootnote().run();
          }
          setShow(false);
        } catch {
        }
        return;
      }

      if (!isShift && key === 'n' && shouldShowMoveToNote && projectId && onMoveToNote) {
        const { state } = editor;
        const { selection } = state;
        const { from, to } = selection;
        if (from === to) {
          event.preventDefault();
          openCreateProjectNoteDialog(projectId);
          setShow(false);
          return;
        }
        event.preventDefault();
        const rawText = getDocumentPlainText(state.doc, from, to);
        const content = rawText.replace(/<[^>]*>/g, '').trim();
        const truncated = content.length > NOTES_CONTENT_MAX_LENGTH
          ? content.slice(0, NOTES_CONTENT_MAX_LENGTH)
          : content;
        if (!truncated) {
          openCreateProjectNoteDialog(projectId);
          setShow(false);
          return;
        }
        editor.chain().focus().deleteSelection().run();
        setShow(false);
        void (async () => {
          try {
            const result = await onMoveToNote(truncated);
            if (result) {
              showToast(
                <>
                  {t('note_created_toast_suffix_before')}
                  <Link
                    to={`/projects/${result.projectId}/notes`}
                    style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
                    onClick={() => hideToast()}
                  >
                    {t('note_created_toast_link_text')}
                  </Link>
                  {t('note_created_toast_suffix_after')}
                </>
              );
            }
          } catch {
            showErrorToast(t('failed_to_create_note') || 'Failed to create note');
          }
        })();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editor, isMac, effectiveActions, shouldShowFootnote, shouldShowMoveToNote, onSynonymsClick, onAnalyzeClick, onCommentClick, sidebarContext, setIsFocusMode, projectId, onMoveToNote, showToast, showErrorToast, hideToast, t, isAnalyzing]);

  if (!editor) {
    return null;
  }

  const formatButtons: FormatButton[] = [];

  if (effectiveActions.includes('format')) {
    formatButtons.push(
      {
        name: 'bold',
        label: `Bold (${modKey}+B)`,
        icon: (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          </svg>
        ),
        command: () => (editor.chain().focus() as any).toggleBold().run(),
        isActive: editor.isActive('bold'),
      },
      {
        name: 'italic',
        label: `Italic (${modKey}+I)`,
        icon: (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="4" x2="10" y2="4" />
            <line x1="14" y1="20" x2="5" y2="20" />
            <line x1="15" y1="4" x2="9" y2="20" />
          </svg>
        ),
        command: () => (editor.chain().focus() as any).toggleItalic().run(),
        isActive: editor.isActive('italic'),
      },
      {
        name: 'h1',
        label: `Heading 1 (${modKey}+${optKey}+1)`,
        icon: (
          <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', fontWeight: 700, color: 'inherit' }}>H1</span>
        ),
        command: () => (editor.chain().focus() as any).toggleHeading({ level: 1 }).run(),
        isActive: editor.isActive('heading', { level: 1 }),
      },
      {
        name: 'h2',
        label: `Heading 2 (${modKey}+${optKey}+2)`,
        icon: (
          <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', fontWeight: 700, color: 'inherit' }}>H2</span>
        ),
        command: () => (editor.chain().focus() as any).toggleHeading({ level: 2 }).run(),
        isActive: editor.isActive('heading', { level: 2 }),
      },
      {
        name: 'h3',
        label: `Heading 3 (${modKey}+${optKey}+3)`,
        icon: (
          <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', fontWeight: 700, color: 'inherit' }}>H3</span>
        ),
        command: () => (editor.chain().focus() as any).toggleHeading({ level: 3 }).run(),
        isActive: editor.isActive('heading', { level: 3 }),
      },
      {
        name: 'strike',
        label: `Strikethrough (${modKey}+Shift+X)`,
        icon: (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4h-5a4 4 0 0 0-4 4v4" />
            <path d="M16 12h-7a4 4 0 0 0 0 8h5a 4 4 0 0 0 4-4V8" />
            <line x1="4" y1="12" x2="20" y2="12" />
          </svg>
        ),
        command: () => (editor.chain().focus() as any).toggleStrike().run(),
        isActive: editor.isActive('strike'),
      },
      ...(shouldShowLists ? [
        {
          name: 'bulletList',
          label: `Bullet List (${modKey}+Shift+8)`,
          icon: (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          ),
          command: () => (editor.chain().focus() as any).toggleBulletList().run(),
          isActive: editor.isActive('bulletList'),
        },
        {
          name: 'orderedList',
          label: `Ordered List (${modKey}+Shift+7)`,
          icon: (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="10" y1="6" x2="21" y2="6" />
              <line x1="10" y1="12" x2="21" y2="12" />
              <line x1="10" y1="18" x2="21" y2="18" />
              <path d="M4 6h1v4h-1" />
              <path d="M4 10c0 1.105 1.12 2 2.5 2s2.5-.895 2.5-2" />
              <path d="M6 18H4c0-1 1-2 2-2s2 1 2 2c0 1-1 2-2 2s-2-1-2-2" />
            </svg>
          ),
          command: () => (editor.chain().focus() as any).toggleOrderedList().run(),
          isActive: editor.isActive('orderedList'),
        },
        {
          name: 'taskList',
          label: `Task List (${modKey}+Shift+9)`,
          icon: (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          ),
          command: () => (editor.chain().focus() as any).toggleTaskList().run(),
          isActive: editor.isActive('taskList'),
        }
      ] : []),
    );
  }

  if (effectiveActions.includes('synonyms') && onSynonymsClick) {
    const selectedText = getDocumentPlainText(
      editor.state.doc,
      editor.state.selection.from,
      editor.state.selection.to,
      ' ',
    );
    const trimmedText = selectedText.trim();
    const wordCount = trimmedText.split(/\s+/).filter(w => w.length > 0).length;
    const sentenceCount = trimmedText.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
    const isDisabled = wordCount > 35 || sentenceCount > 3;
    const synonymsLabel = wordCount > 1 ? t('alternate_wordings') : t('synonyms');

    formatButtons.push({
      name: 'synonyms',
      label: isDisabled ? `${t('select_shorter_text_for_alternatives')} (${modKey}+${optKey}+S)` : `${synonymsLabel} (${modKey}+${optKey}+S)`,
      icon: (
        <BookType className={lucideIconSize} />
      ),
      command: () => {
        if (!isDisabled) {
          onSynonymsClick();
          setShow(false);
        }
      },
      isActive: false,
      disabled: isDisabled,
      disabledTooltip: t('select_shorter_text_for_alternatives'),
      hasSeparatorBefore: formatButtons.length > 0,
    });
  }

  if (shouldShowFootnote) {
    formatButtons.push({
      name: 'footnote',
      label: `${t('add_footnote')} (${modKey}+${optKey}+F)`,
      icon: (
        <span style={{ fontSize: isMobile ? '0.75rem' : '1rem', fontWeight: 700, color: 'inherit' }}>
          ab<sup style={{ fontSize: '0.6em' }}>1</sup>
        </span>
      ),
      command: () => {
        try {
          const { state } = editor;
          const { selection } = state;
          const { from, to } = selection;

          if (from !== to) {
            editor.chain()
              .setTextSelection(to)
              .focus()
              .addFootnote()
              .run();
          } else {
            (editor.chain().focus() as any).addFootnote().run();
          }
          setShow(false);
        } catch (error) {
          console.error('Failed to insert footnote:', error);
        }
      },
      isActive: false,
    });
  }

  if (shouldShowMoveToNote && projectId && onMoveToNote) {
    formatButtons.push({
      name: 'moveToNote',
      label: `${t('move_to_note')} (${modKey}+${optKey}+N)`,
      icon: (
        <NoteIcon className={lucideIconSize} />
      ),
      command: async () => {
        const { state } = editor;
        const { selection } = state;
        const { from, to } = selection;
        if (from === to) {
          setShow(false);
          return;
        }
        const rawText = getDocumentPlainText(state.doc, from, to);
        const content = rawText.replace(/<[^>]*>/g, '').trim();
        const truncated = content.length > NOTES_CONTENT_MAX_LENGTH
          ? content.slice(0, NOTES_CONTENT_MAX_LENGTH)
          : content;
        if (!truncated) {
          setShow(false);
          return;
        }
        editor.chain().focus().deleteSelection().run();
        setShow(false);
        try {
          const result = await onMoveToNote(truncated);
          if (result) {
            showToast(
              <>
                {t('note_created_toast_suffix_before')}
                <Link
                  to={`/projects/${result.projectId}/notes`}
                  style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
                  onClick={() => hideToast()}
                >
                  {t('note_created_toast_link_text')}
                </Link>
                {t('note_created_toast_suffix_after')}
              </>
            );
          }
        } catch {
          showErrorToast(t('failed_to_create_note') || 'Failed to create note');
        }
      },
      isActive: false,
    });
  }

  if (effectiveActions.includes('analyze') && onAnalyzeClick) {
    const analyzeDisableReason = getFeedbackAnalyzeDisableReason(editor);
    const isAnalyzeDisabled = analyzeDisableReason !== null;
    const disabledTooltip = isAnalyzing
      ? t('ongoing_analysis_please_wait')
      : getFeedbackAnalyzeDisableTooltip(analyzeDisableReason, t);

    formatButtons.push({
      name: 'analyze',
      label: isAnalyzing
        ? t('analyzing')
        : `${t('analyze')} (${modKey}+${optKey}+A)`,
      icon: isAnalyzing ? (
        <Loader2 className={`${lucideIconSize} animate-spin`} />
      ) : (
        <Lightbulb className={lucideIconSize} />
      ),
      command: () => {
        if (isAnalyzing || isAnalyzeDisabled) {
          return;
        }
        onAnalyzeClick();
      },
      isActive: false,
      disabled: isAnalyzeDisabled && !isAnalyzing,
      disabledTooltip: disabledTooltip,
      hasSeparatorBefore: formatButtons.length > 0,
    });
  }

  if (effectiveActions.includes('chat') && sidebarContext?.openChat) {
    formatButtons.push({
      name: 'chat',
      label: `${t('chat_with_document')} (${modKey}+${optKey}+D)`,
      icon: (
        <Bot className={lucideIconSize} />
      ),
      command: () => {
        suppressShowRef.current = true;
        setTimeout(() => {
          suppressShowRef.current = false;
        }, 500);
        openChatWithCurrentSelection();
        setShow(false);
      },
      isActive: activeView === 'chat' || activeMainAreaView === 'chat',
    });
  }

  if (effectiveActions.includes('comment') && onCommentClick) {
    formatButtons.push({
      name: 'comment',
      label: `${t('add_comment')} (${modKey}+${optKey}+M)`,
      icon: (
        <MessageSquare className={lucideIconSize} />
      ),
      command: () => {
        onCommentClick();
        setShow(false);
      },
      isActive: false,
    });
  }

  if (effectiveActions.includes('readAloud') && isDocumentSpeechSupported()) {
    formatButtons.push({
      name: 'readAloud',
      label: t('read_aloud'),
      icon: <Volume2 className={lucideIconSize} />,
      command: () => {
        const { from, to } = editor.state.selection;
        if (from === to) {
          setShow(false);
          return;
        }
        const text = getDocumentPlainText(editor.state.doc, from, to);
        if (!text.trim()) {
          setShow(false);
          return;
        }
        ensureDocumentSpeechLanguage(toBcp47Locale(locale));
        const started = speakDocumentText(text, {
          sourceRange: { from, to },
        });
        if (started) {
          void trackFeatureUsage({
            feature: 'document_read_aloud',
            documentId,
            token,
            masterKey,
          });
        }
        setShow(false);
      },
      isActive: false,
      hasSeparatorBefore: formatButtons.length > 0,
    });
  }

  const shouldShow = !isBlockedByEditorOverlay && (show || (isAnalyzing && hasSelection));

  const primaryButtonNames = ['bold', 'italic', 'synonyms', 'analyze', 'comment', 'chat', 'readAloud'];
  const primaryButtons = isMobile ? formatButtons.filter((b) => primaryButtonNames.includes(b.name)) : formatButtons;
  const overflowButtons = isMobile ? formatButtons.filter((b) => !primaryButtonNames.includes(b.name)) : [];

  const renderButton = (button: FormatButton) => (
    <React.Fragment key={button.name}>
      {button.hasSeparatorBefore && !isMobile && (
        <div
          style={{
            width: '1px',
            height: isMobile ? '16px' : '20px',
            backgroundColor: COLOR_FORMATTING_BAR_SEPARATOR,
            margin: isMobile ? '0 0.125rem' : '0 0.25rem',
          }}
        />
      )}
      <button
        onClick={button.command}
        title={button.disabledTooltip || button.label}
        disabled={button.disabled}
        aria-busy={button.name === 'analyze' && isAnalyzing ? true : undefined}
        style={{
          background: button.isActive ? 'var(--primary)' : 'transparent',
          border: 'none',
          cursor: button.disabled || (button.name === 'analyze' && isAnalyzing) ? 'wait' : 'pointer',
          padding: isMobile ? '0.375rem' : '0.5rem',
          borderRadius: '0.375rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: button.isActive
            ? COLOR_FORMATTING_BAR_TEXT_ACTIVE
            : button.disabled
              ? 'var(--muted-foreground)'
              : COLOR_FORMATTING_BAR_TEXT,
          opacity: button.disabled ? 0.5 : 1,
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!button.isActive && !button.disabled && !(button.name === 'analyze' && isAnalyzing)) {
            e.currentTarget.style.backgroundColor = COLOR_FORMATTING_BAR_HOVER;
            e.currentTarget.style.color = 'var(--accent-foreground)';
          }
        }}
        onMouseLeave={(e) => {
          if (!button.isActive && !button.disabled) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = COLOR_FORMATTING_BAR_TEXT;
          }
        }}
        aria-label={
          button.name === 'analyze' && isAnalyzing
            ? button.label
            : (button.disabledTooltip || button.label)
        }
      >
        {button.icon}
      </button>
    </React.Fragment>
  );

  return createPortal(
    <div
      ref={barRef}
      data-formatting-bar=""
      className={cn(STICKY_GLASS_CHROME_CLASSNAME, 'rounded-lg shadow-md')}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.125rem' : '0.25rem',
        padding: isMobile ? '0.375rem' : '0.5rem',
        zIndex: FORMATTING_BAR_Z_INDEX,
        transform: 'translateX(-50%)',
        transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
        opacity: shouldShow ? 1 : 0,
        pointerEvents: shouldShow ? 'auto' : 'none',
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {primaryButtons.map(renderButton)}
      {isMobile && overflowButtons.length > 0 && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title={t('format_more')}
                aria-label={t('format_more')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.375rem',
                  borderRadius: '0.375rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: COLOR_FORMATTING_BAR_TEXT,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = COLOR_FORMATTING_BAR_HOVER;
                  e.currentTarget.style.color = 'var(--accent-foreground)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLOR_FORMATTING_BAR_TEXT;
                }}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="center">
              {overflowButtons.map((button) => (
                <React.Fragment key={button.name}>
                  {button.hasSeparatorBefore && !isMobile && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    disabled={button.disabled}
                    onSelect={(e) => {
                      e.preventDefault();
                      if (!button.disabled) {
                        button.command();
                      }
                    }}
                  >
                    {button.icon}
                    <span style={{ marginLeft: '0.5rem' }}>{button.label}</span>
                  </DropdownMenuItem>
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>,
    document.body,
  );
};

